import { supabase } from './supabase';
import { fetchAllRows } from './fetchAll';
import { dayKeyInTz } from '../components/OverviewDrilldownModal';

/**
 * LIFECYCLE ENGAGEMENT — the measurement layer behind `/engagement`.
 *
 * Everything the Engagement page shows is computed here, from four narrow selects, so the page
 * component stays a renderer and the definitions live in one auditable place. Same design as
 * `AnalyticsScreen`: fetch once, reduce many times, so every figure describes the SAME instant.
 *
 * ── ⚠️ THE ONE THING TO READ BEFORE CHANGING ANYTHING HERE ────────────────────────────────────
 * ACTIVITY IS MEASURED IN RATINGS, NOT APP OPENS — and that is forced on us, not preferred.
 *
 * The obvious source for "did this user come back" is `app_sessions` or `app_events`. Both are
 * populated and both are admin-readable. Both are also, measurably, NOT a record of app usage:
 *
 *   · `app_sessions` held 171 rows across 115 users over ~2 months against 474 accounts, and 104
 *     of those 115 users had EXACTLY ONE row, ever. It behaves like a first-open marker.
 *   · `app_events` is richer (~6k rows) but stops after signup day for almost everyone: 442 of the
 *     460 users with any event had exactly one active DAY in their whole lifetime.
 *   · The proof it under-fires rather than describing real behaviour: 128 of 194 raters have a
 *     RATING timestamped later than their most recent app_event, and 80 raters have more distinct
 *     rating-days than event-days. People are demonstrably using the app on days the event stream
 *     never records.
 *
 * A rating, by contrast, is a server-written row that cannot happen without a session. It
 * under-counts engagement (opening the app and not rating is invisible) but it never invents it.
 * For a re-engagement workstream an under-count is survivable; a fabricated DAU is not — you would
 * size win-back audiences off a number that is wrong in an unknown direction.
 *
 * So: every headline metric below reads `logs`. `app_events` / `app_sessions` are fetched ONLY to
 * feed `telemetryHealth`, which exists to keep the discrepancy visible on screen instead of buried
 * in this comment. ⚠️ Do not promote either one to a headline metric until `telemetryHealth` shows
 * the gap has closed — see the build-out plan, phase 0.
 *
 * ── DAY AND WEEK BOUNDARIES ───────────────────────────────────────────────────────────────────
 * Days are bucketed with the shared `dayKeyInTz` (midnight America/Chicago), the same helper the
 * Home charts use, so a "day" means the same thing on every screen. ⚠️ Nightlife arguably wants a
 * 6am→6am boundary — a rating logged at 1:30am Saturday is Friday night's — but adopting one here
 * only would make this page disagree with Overview for the sake of a handful of rows. It is listed
 * as a refinement in the build-out plan rather than silently applied.
 *
 * Weeks are Monday-start, matching Postgres `date_trunc('week', …)` used elsewhere.
 */

// ---------------------------------------------------------------------------
// Row shapes — deliberately minimal. Row count is what scales here, so every
// extra column is paid once per rating / event, forever.
// ---------------------------------------------------------------------------

export type RatingRow = { user_id: string | null; created_at: string };
export type AccountRow = { id: string; created_at: string; referral_source: string | null };
export type PulseRow = { user_id: string | null; created_at: string };
export type SessionRow = { user_id: string | null; started_at: string };

/** Result of actually attempting a read, so the page states facts rather than assumptions. */
export type ReadProbe = {
  table: string;
  what: string;
  status: 'readable' | 'own_rows_only' | 'blocked';
  detail: string;
};

export type EngagementSource = {
  ratings: RatingRow[];
  accounts: AccountRow[];
  pulses: PulseRow[];
  sessions: SessionRow[];
  probes: ReadProbe[];
  fetchedAt: number;
};

// ---------------------------------------------------------------------------
// Tunables — every threshold the page asserts, named once.
// ---------------------------------------------------------------------------

/** Rated within this many days = "Active". Chosen because the product's natural cadence is weekly. */
export const ACTIVE_WINDOW_DAYS = 7;
/** Beyond this and a user is no longer merely between weekends — they have slipped. */
export const SLIPPING_WINDOW_DAYS = 28;
/** Beyond this, a nudge is a win-back, not a reminder. */
export const DORMANT_WINDOW_DAYS = 90;
/**
 * A gap of three weeks is the lapse definition used for win-back sizing. Two weeks over-counts —
 * plenty of people simply skip a fortnight of going out — and a month is too late to intervene.
 */
export const LAPSE_DAYS = 21;
/** How many weeks-since-signup the cohort grid shows. */
export const COHORT_WEEKS = 6;

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const MS_DAY = 86_400_000;

function median(xs: number[]): number | null {
  if (!xs.length) return null;
  const s = xs.slice().sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

/** Whole days between two instants, floored — "today" reads as 0, not 0.4. */
function daysBetween(a: number, b: number): number {
  return Math.floor((a - b) / MS_DAY);
}

/**
 * A `YYYY-MM-DD` day key as an integer day number. The key is a CALENDAR date (already resolved to
 * the DAY_TZ wall clock by `dayKeyInTz`), so parsing it as UTC is correct — it is not an instant
 * being converted, it is a date being indexed. Subtracting two of these gives whole days with no
 * DST arithmetic to get wrong.
 */
function dayIndex(dayKey: string): number {
  const [y, m, d] = dayKey.split('-').map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / MS_DAY);
}

/** Monday-start week key for a `YYYY-MM-DD` day key. */
export function weekKeyFromDayKey(dayKey: string): string {
  const [y, m, d] = dayKey.split('-').map(Number);
  const t = Date.UTC(y, m - 1, d);
  const back = (new Date(t).getUTCDay() + 6) % 7; // 0=Sun → 6 days back to Monday
  return new Date(t - back * MS_DAY).toISOString().slice(0, 10);
}

/** Week index (Monday-aligned) so consecutive weeks differ by exactly 1. */
function weekIndex(weekKey: string): number {
  return Math.round(dayIndex(weekKey) / 7);
}

export function pctOf(n: number, d: number): number | null {
  return d > 0 ? Math.round((n / d) * 1000) / 10 : null;
}

// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------

/**
 * ⚠️ THESE PROBES ARE NOT DECORATION. Half of the lifecycle workstream is notifications, and the
 * tables that would measure notifications are not readable from this dashboard. Rather than
 * hard-coding that claim (which would silently go stale the day someone adds the policy), the page
 * ATTEMPTS each read and reports what actually came back. When the migrations in the build-out plan
 * land, these flip to `readable` on their own with no code change.
 *
 * `notifications` is the subtle one: it does not error, it just returns the signed-in admin's own
 * rows, because `notifications_select_own` is the only SELECT policy on it. A count alone would
 * look like a working global read, so the probe counts DISTINCT user_id in the sample — one user
 * (or none) is proof the scope is per-user, not global.
 */
async function runReadProbes(): Promise<ReadProbe[]> {
  const probes: ReadProbe[] = [];

  try {
    const { data, error } = await supabase.from('notifications').select('user_id').limit(1000);
    if (error) {
      probes.push({
        table: 'notifications',
        what: 'Delivery + read-rate of every in-app notification',
        status: 'blocked',
        detail: error.message,
      });
    } else {
      const rows = data ?? [];
      const distinct = new Set(rows.map((r) => (r as { user_id: string | null }).user_id).filter(Boolean)).size;
      probes.push({
        table: 'notifications',
        what: 'Delivery + read-rate of every in-app notification',
        status: distinct > 1 ? 'readable' : 'own_rows_only',
        detail:
          distinct > 1
            ? `${rows.length} rows spanning ${distinct} users — a global read now works.`
            : `${rows.length} rows spanning ${distinct} user(s) — RLS scopes this to your own notifications. There is no admin SELECT policy, so notification performance cannot be measured from here.`,
      });
    }
  } catch (e) {
    probes.push({
      table: 'notifications',
      what: 'Delivery + read-rate of every in-app notification',
      status: 'blocked',
      detail: e instanceof Error ? e.message : 'Read threw',
    });
  }

  try {
    const { count, error } = await supabase
      .from('user_push_tokens')
      .select('id', { count: 'exact', head: true });
    if (error) {
      probes.push({
        table: 'user_push_tokens',
        what: 'How many users can receive a push at all',
        status: 'blocked',
        detail: error.message,
      });
    } else {
      probes.push({
        table: 'user_push_tokens',
        what: 'How many users can receive a push at all',
        status: (count ?? 0) > 0 ? 'readable' : 'blocked',
        detail:
          (count ?? 0) > 0
            ? `${count} token rows visible.`
            : 'Row-level security is enabled with no SELECT policy for this role, so the table reads as empty rather than erroring. Push reachability is unmeasurable until a policy exists.',
      });
    }
  } catch (e) {
    probes.push({
      table: 'user_push_tokens',
      what: 'How many users can receive a push at all',
      status: 'blocked',
      detail: e instanceof Error ? e.message : 'Read threw',
    });
  }

  try {
    const { error } = await supabase.from('profiles').select('notification_preferences').limit(1);
    probes.push({
      table: 'profiles.notification_preferences',
      what: 'Who has muted which notification category',
      status: error ? 'blocked' : 'readable',
      detail: error
        ? `${error.message} — the column is excluded from the authenticated column grant on profiles, so opt-out rates cannot be read.`
        : 'Column grant present.',
    });
  } catch (e) {
    probes.push({
      table: 'profiles.notification_preferences',
      what: 'Who has muted which notification category',
      status: 'blocked',
      detail: e instanceof Error ? e.message : 'Read threw',
    });
  }

  return probes;
}

/**
 * ⚠️ EVERY PAGED SELECT CARRIES AN EXPLICIT ORDER BY, and that is a correctness requirement, not a
 * preference. `fetchAllRows` walks the table with `.range(from, to)`; without a deterministic sort
 * Postgres is free to return the pages in different physical orders, which silently DROPS rows at
 * the page boundaries — and a lifecycle metric that quietly loses a slice of ratings is wrong in a
 * direction nobody can see. `profiles` sorts by its primary key (unique, so pagination is exactly
 * stable); the time-series tables sort by their timestamp, which is indexed where it matters.
 */
export async function fetchEngagementSource(): Promise<EngagementSource> {
  const [ratings, accounts, pulses, sessions, probes] = await Promise.all([
    fetchAllRows<RatingRow>((from, to) =>
      supabase
        .from('logs')
        .select('user_id, created_at')
        .is('retired_at', null)
        .is('deleted_at', null)
        .eq('post_kind', 'rating')
        .order('created_at', { ascending: true })
        .range(from, to),
    ),
    fetchAllRows<AccountRow>((from, to) =>
      supabase
        .from('profiles')
        .select('id, created_at, referral_source')
        .order('id', { ascending: true })
        .range(from, to),
    ),
    fetchAllRows<PulseRow>((from, to) =>
      supabase
        .from('app_events')
        .select('user_id, created_at')
        .order('created_at', { ascending: true })
        .range(from, to),
    ),
    fetchAllRows<SessionRow>((from, to) =>
      supabase
        .from('app_sessions')
        .select('user_id, started_at')
        .order('started_at', { ascending: true })
        .range(from, to),
    ),
    runReadProbes(),
  ]);

  return { ratings, accounts, pulses, sessions, probes, fetchedAt: Date.now() };
}

// ---------------------------------------------------------------------------
// Computed shapes
// ---------------------------------------------------------------------------

export type LifecycleKey = 'active' | 'slipping' | 'dormant' | 'lost' | 'newNoRating' | 'neverRated';

export type LifecycleSegment = {
  key: LifecycleKey;
  label: string;
  /** The lifecycle intervention this segment is the audience for. */
  play: string;
  count: number;
  pct: number | null;
  /** Median days since last rating; null where the segment has never rated. */
  medianDaysSince: number | null;
  tone: 'success' | 'warning' | 'danger' | 'neutral' | 'accent';
};

export type StreakBucket = { len: number; users: number };

export type StreakStats = {
  /** Runs still alive: they include this week or last week. */
  current: StreakBucket[];
  longest: StreakBucket[];
  onRunNow: number;
  bestEver: number;
  ratersWithAnyRatingWeek: number;
};

export type WinbackStats = {
  currentlyLapsed: number;
  everLapsed: number;
  resurrected: number;
  resurrectionRate: number | null;
  medianReturnGapDays: number | null;
  maxReturnGapDays: number | null;
};

export type WeekPoint = { week: string; label: string; raters: number; ratings: number; signups: number };

export type CohortRow = {
  week: string;
  label: string;
  size: number;
  /** Retention % for weeks 0..COHORT_WEEKS-1; null where the window has not fully elapsed. */
  cells: (number | null)[];
};

export type SourceRow = {
  source: string;
  accounts: number;
  activated: number;
  activatedPct: number | null;
  active28: number;
  active28Pct: number | null;
};

export type TelemetryHealth = {
  accounts: number;
  sessionRows: number;
  sessionUsers: number;
  sessionUsersWithOneRow: number;
  lastSessionAt: string | null;
  eventRows: number;
  eventUsers: number;
  eventUsersWithOneActiveDay: number;
  lastEventAt: string | null;
  /** Raters whose newest RATING is later than their newest app_event — the under-fire proof. */
  ratersRatingNewerThanEvent: number;
  ratersComparable: number;
};

export type Engagement = {
  accounts: number;
  raters: number;
  activationPct: number | null;
  medianDaysToFirstRating: number | null;
  sameDayActivationPct: number | null;
  segments: LifecycleSegment[];
  streaks: StreakStats;
  winback: WinbackStats;
  weeks: WeekPoint[];
  cohorts: CohortRow[];
  sources: SourceRow[];
  telemetry: TelemetryHealth;
  probes: ReadProbe[];
  ratingsCounted: number;
  fetchedAt: number;
};

// ---------------------------------------------------------------------------
// Compute
// ---------------------------------------------------------------------------

export function computeEngagement(src: EngagementSource): Engagement {
  const now = src.fetchedAt;
  const todayIndex = dayIndex(dayKeyInTz(new Date(now)));

  /**
   * One pass to bucket ratings by user. `days` is a SET of day keys, not a list of timestamps:
   * every metric on this page is about which days someone showed up, never about how many times
   * they showed up within one day, and collapsing early keeps the streak and gap scans linear.
   */
  const byUser = new Map<string, Set<string>>();
  const weekAgg = new Map<string, { raters: Set<string>; ratings: number; signups: number }>();

  const touchWeek = (week: string) => {
    let w = weekAgg.get(week);
    if (!w) {
      w = { raters: new Set<string>(), ratings: 0, signups: 0 };
      weekAgg.set(week, w);
    }
    return w;
  };

  src.ratings.forEach((r) => {
    if (!r.user_id) return;
    const day = dayKeyInTz(r.created_at);
    let s = byUser.get(r.user_id);
    if (!s) {
      s = new Set<string>();
      byUser.set(r.user_id, s);
    }
    s.add(day);
    const w = touchWeek(weekKeyFromDayKey(day));
    w.ratings += 1;
    w.raters.add(r.user_id);
  });

  src.accounts.forEach((a) => {
    touchWeek(weekKeyFromDayKey(dayKeyInTz(a.created_at))).signups += 1;
  });

  /** Per-user derived facts, computed once and reused by every section below. */
  type UserFacts = {
    days: number[]; // ascending day indices
    firstDay: number;
    lastDay: number;
    longestWeekRun: number;
    currentWeekRun: number; // 0 when the run does not reach this week or last
    gapsAtOrOverLapse: number[];
  };

  const thisWeekIndex = weekIndex(weekKeyFromDayKey(dayKeyInTz(new Date(now))));
  const facts = new Map<string, UserFacts>();

  byUser.forEach((daySet, uid) => {
    const days: number[] = [];
    daySet.forEach((k) => days.push(dayIndex(k)));
    days.sort((a, b) => a - b);

    const weekSet = new Set<number>();
    daySet.forEach((k) => weekSet.add(weekIndex(weekKeyFromDayKey(k))));
    const weeks: number[] = [];
    weekSet.forEach((w) => weeks.push(w));
    weeks.sort((a, b) => a - b);

    // Longest / current run of CONSECUTIVE rating weeks.
    let longest = 1;
    let run = 1;
    let runEnd = weeks[0];
    let bestEndOfRunTouchingNow = 0;
    for (let i = 1; i < weeks.length; i++) {
      if (weeks[i] === weeks[i - 1] + 1) run += 1;
      else run = 1;
      runEnd = weeks[i];
      if (run > longest) longest = run;
    }
    /**
     * ⚠️ "Current" tolerates a one-week grace. The week in progress is incomplete — someone who
     * goes out every Saturday has not broken their streak on a Tuesday — so a run whose last week
     * is either this week or last week is still alive. Without the grace, this number would
     * collapse to near zero every Monday morning and look like a crash.
     */
    if (runEnd >= thisWeekIndex - 1) bestEndOfRunTouchingNow = run;

    const gaps: number[] = [];
    for (let i = 1; i < days.length; i++) {
      const g = days[i] - days[i - 1];
      if (g >= LAPSE_DAYS) gaps.push(g);
    }

    facts.set(uid, {
      days,
      firstDay: days[0],
      lastDay: days[days.length - 1],
      longestWeekRun: longest,
      currentWeekRun: bestEndOfRunTouchingNow,
      gapsAtOrOverLapse: gaps,
    });
  });

  // ── Lifecycle segments ────────────────────────────────────────────────────
  const bucket: Record<LifecycleKey, number[]> = {
    active: [],
    slipping: [],
    dormant: [],
    lost: [],
    newNoRating: [],
    neverRated: [],
  };

  src.accounts.forEach((a) => {
    const f = facts.get(a.id);
    if (!f) {
      const ageDays = daysBetween(now, new Date(a.created_at).getTime());
      // Someone who joined this week has not failed to come back — they have not been asked yet.
      if (ageDays <= ACTIVE_WINDOW_DAYS) bucket.newNoRating.push(ageDays);
      else bucket.neverRated.push(ageDays);
      return;
    }
    const since = todayIndex - f.lastDay;
    if (since <= ACTIVE_WINDOW_DAYS) bucket.active.push(since);
    else if (since <= SLIPPING_WINDOW_DAYS) bucket.slipping.push(since);
    else if (since <= DORMANT_WINDOW_DAYS) bucket.dormant.push(since);
    else bucket.lost.push(since);
  });

  const accounts = src.accounts.length;
  const seg = (
    key: LifecycleKey,
    label: string,
    play: string,
    tone: LifecycleSegment['tone'],
    hasRated: boolean,
  ): LifecycleSegment => ({
    key,
    label,
    play,
    count: bucket[key].length,
    pct: pctOf(bucket[key].length, accounts),
    medianDaysSince: hasRated ? median(bucket[key]) : null,
    tone,
  });

  const segments: LifecycleSegment[] = [
    seg('active', 'Active', 'Nothing. Do not push these people — protect the streak instead.', 'success', true),
    seg(
      'slipping',
      'Slipping',
      'The highest-value audience on this page: still in the habit, one missed weekend from leaving. Friend-activity nudge.',
      'warning',
      true,
    ),
    seg('dormant', 'Dormant', 'Win-back with new information — what changed in their city since they left.', 'danger', true),
    seg('lost', 'Lost', 'Do not spend pushes here until win-back is proven on Dormant.', 'neutral', true),
    seg('newNoRating', 'New — no rating yet', 'Onboarding, not lifecycle. They joined inside the last week.', 'accent', false),
    seg(
      'neverRated',
      'Never rated',
      'An activation failure, not a retention one. Rating #1 is the gate every metric here sits behind.',
      'neutral',
      false,
    ),
  ];

  // ── Streaks ───────────────────────────────────────────────────────────────
  const currentCount = new Map<number, number>();
  const longestCount = new Map<number, number>();
  let onRunNow = 0;
  let bestEver = 0;
  facts.forEach((f) => {
    longestCount.set(f.longestWeekRun, (longestCount.get(f.longestWeekRun) ?? 0) + 1);
    if (f.longestWeekRun > bestEver) bestEver = f.longestWeekRun;
    if (f.currentWeekRun > 0) {
      currentCount.set(f.currentWeekRun, (currentCount.get(f.currentWeekRun) ?? 0) + 1);
      if (f.currentWeekRun >= 2) onRunNow += 1;
    }
  });
  const toBuckets = (m: Map<number, number>): StreakBucket[] =>
    Array.from(m.entries())
      .map(([len, users]) => ({ len, users }))
      .sort((a, b) => a.len - b.len);

  const streaks: StreakStats = {
    current: toBuckets(currentCount),
    longest: toBuckets(longestCount),
    onRunNow,
    bestEver,
    ratersWithAnyRatingWeek: facts.size,
  };

  // ── Win-back ──────────────────────────────────────────────────────────────
  /**
   * "Ever lapsed" is the honest denominator: everyone who has EITHER gone quiet for ≥LAPSE_DAYS
   * mid-history (and therefore had a chance to return) OR is quiet right now. Dividing returns by
   * "currently lapsed" alone would flatter the rate, because the people who came back are by
   * definition no longer in that group.
   */
  let currentlyLapsed = 0;
  let resurrected = 0;
  let everLapsed = 0;
  const returnGaps: number[] = [];
  facts.forEach((f) => {
    const lapsedNow = todayIndex - f.lastDay >= LAPSE_DAYS;
    const returnedBefore = f.gapsAtOrOverLapse.length > 0;
    if (lapsedNow) currentlyLapsed += 1;
    if (returnedBefore) {
      resurrected += 1;
      f.gapsAtOrOverLapse.forEach((g) => returnGaps.push(g));
    }
    if (lapsedNow || returnedBefore) everLapsed += 1;
  });

  const winback: WinbackStats = {
    currentlyLapsed,
    everLapsed,
    resurrected,
    resurrectionRate: pctOf(resurrected, everLapsed),
    medianReturnGapDays: median(returnGaps),
    maxReturnGapDays: returnGaps.length ? returnGaps.reduce((a, b) => (b > a ? b : a), 0) : null,
  };

  // ── Weekly series ─────────────────────────────────────────────────────────
  const weeks: WeekPoint[] = Array.from(weekAgg.entries())
    .map(([week, v]) => ({
      week,
      label: `${week.slice(5, 7)}/${week.slice(8, 10)}`,
      raters: v.raters.size,
      ratings: v.ratings,
      signups: v.signups,
    }))
    .sort((a, b) => (a.week < b.week ? -1 : 1));

  // ── Cohort retention ──────────────────────────────────────────────────────
  /**
   * Retained in week N = rated at least once in [signup + 7N, signup + 7(N+1)). A cell is null —
   * not zero — when the cohort has not lived long enough for that window to close; counting an
   * unfinished window as a miss drags every recent cohort down and makes retention look like it is
   * collapsing every time signups grow. Same rule as `AnalyticsScreen`'s weekN.
   */
  const cohortMap = new Map<string, { size: number; hit: number[]; eligible: number[] }>();
  src.accounts.forEach((a) => {
    const joinedMs = new Date(a.created_at).getTime();
    const joinedDay = dayIndex(dayKeyInTz(a.created_at));
    const week = weekKeyFromDayKey(dayKeyInTz(a.created_at));
    let c = cohortMap.get(week);
    if (!c) {
      c = {
        size: 0,
        hit: new Array(COHORT_WEEKS).fill(0),
        eligible: new Array(COHORT_WEEKS).fill(0),
      };
      cohortMap.set(week, c);
    }
    c.size += 1;
    const ageDays = daysBetween(now, joinedMs);
    const f = facts.get(a.id);
    for (let n = 0; n < COHORT_WEEKS; n++) {
      if (ageDays < (n + 1) * 7) break; // window still open — leave it null
      c.eligible[n] += 1;
      if (!f) continue;
      const lo = joinedDay + n * 7;
      const hi = joinedDay + (n + 1) * 7;
      if (f.days.some((d) => d >= lo && d < hi)) c.hit[n] += 1;
    }
  });

  const cohorts: CohortRow[] = Array.from(cohortMap.entries())
    .map(([week, c]) => ({
      week,
      label: `${week.slice(5, 7)}/${week.slice(8, 10)}`,
      size: c.size,
      cells: c.hit.map((h, i) => (c.eligible[i] > 0 ? pctOf(h, c.eligible[i]) : null)),
    }))
    .sort((a, b) => (a.week < b.week ? -1 : 1));

  // ── Acquisition source × retention ────────────────────────────────────────
  const sourceMap = new Map<string, { accounts: number; activated: number; active28: number }>();
  src.accounts.forEach((a) => {
    const key = (a.referral_source ?? '').trim() || 'unknown';
    let s = sourceMap.get(key);
    if (!s) {
      s = { accounts: 0, activated: 0, active28: 0 };
      sourceMap.set(key, s);
    }
    s.accounts += 1;
    const f = facts.get(a.id);
    if (f) {
      s.activated += 1;
      if (todayIndex - f.lastDay <= SLIPPING_WINDOW_DAYS) s.active28 += 1;
    }
  });

  const sources: SourceRow[] = Array.from(sourceMap.entries())
    .map(([source, s]) => ({
      source,
      accounts: s.accounts,
      activated: s.activated,
      activatedPct: pctOf(s.activated, s.accounts),
      active28: s.active28,
      active28Pct: pctOf(s.active28, s.accounts),
    }))
    .sort((a, b) => b.accounts - a.accounts);

  // ── Activation speed ──────────────────────────────────────────────────────
  const toFirst: number[] = [];
  let sameDay = 0;
  src.accounts.forEach((a) => {
    const f = facts.get(a.id);
    if (!f) return;
    const d = f.firstDay - dayIndex(dayKeyInTz(a.created_at));
    // Negative is impossible in principle; clamp rather than let a clock skew poison the median.
    const clamped = d < 0 ? 0 : d;
    toFirst.push(clamped);
    if (clamped === 0) sameDay += 1;
  });

  // ── Telemetry health ──────────────────────────────────────────────────────
  const sessionsPerUser = new Map<string, number>();
  let lastSessionAt: string | null = null;
  let lastSessionMs = -Infinity;
  src.sessions.forEach((s) => {
    if (s.user_id) sessionsPerUser.set(s.user_id, (sessionsPerUser.get(s.user_id) ?? 0) + 1);
    /* Compared as instants, not strings — Postgres varies the fractional-second width between
       rows, so lexicographic ordering of the ISO text is not reliably chronological. */
    const t = new Date(s.started_at).getTime();
    if (t > lastSessionMs) {
      lastSessionMs = t;
      lastSessionAt = s.started_at;
    }
  });
  let sessionUsersWithOneRow = 0;
  sessionsPerUser.forEach((n) => {
    if (n === 1) sessionUsersWithOneRow += 1;
  });

  const eventDays = new Map<string, Set<string>>();
  const lastEventDay = new Map<string, number>();
  let lastEventAt: string | null = null;
  let lastEventMs = -Infinity;
  src.pulses.forEach((p) => {
    const t = new Date(p.created_at).getTime();
    if (t > lastEventMs) {
      lastEventMs = t;
      lastEventAt = p.created_at;
    }
    if (!p.user_id) return;
    const key = dayKeyInTz(p.created_at);
    let s = eventDays.get(p.user_id);
    if (!s) {
      s = new Set<string>();
      eventDays.set(p.user_id, s);
    }
    s.add(key);
    const idx = dayIndex(key);
    const prev = lastEventDay.get(p.user_id);
    if (prev === undefined || idx > prev) lastEventDay.set(p.user_id, idx);
  });
  let eventUsersWithOneActiveDay = 0;
  eventDays.forEach((s) => {
    if (s.size === 1) eventUsersWithOneActiveDay += 1;
  });

  let ratersRatingNewerThanEvent = 0;
  let ratersComparable = 0;
  facts.forEach((f, uid) => {
    const le = lastEventDay.get(uid);
    if (le === undefined) return;
    ratersComparable += 1;
    if (f.lastDay > le) ratersRatingNewerThanEvent += 1;
  });

  const telemetry: TelemetryHealth = {
    accounts,
    sessionRows: src.sessions.length,
    sessionUsers: sessionsPerUser.size,
    sessionUsersWithOneRow,
    lastSessionAt,
    eventRows: src.pulses.length,
    eventUsers: eventDays.size,
    eventUsersWithOneActiveDay,
    lastEventAt,
    ratersRatingNewerThanEvent,
    ratersComparable,
  };

  return {
    accounts,
    raters: facts.size,
    activationPct: pctOf(facts.size, accounts),
    medianDaysToFirstRating: median(toFirst),
    sameDayActivationPct: pctOf(sameDay, facts.size),
    segments,
    streaks,
    winback,
    weeks,
    cohorts,
    sources,
    telemetry,
    probes: src.probes,
    ratingsCounted: src.ratings.length,
    fetchedAt: src.fetchedAt,
  };
}
